import { FileAttributeProps, FileValueType } from 'Types/Attribute.types'
import Button from 'antd/lib/button'
import Tooltip from 'antd/lib/tooltip'
import Upload from 'antd/lib/upload/Upload'
import React, { useCallback, useMemo } from 'react'
import './File.less'
import { RcFile } from 'antd/lib/upload'
import { getFileDownloadUrl, uploadFile } from 'Services/tasks.service'
import { useParams } from 'react-router-dom'
import { useMessage } from 'shared/hooks/use-message'
import { downloadPublicFile } from 'Utils/common.utils'
import { FilePreview } from 'Components/attributes/FileAttribute/FilePreview'
import CloudDownloadOutlined from '@ant-design/icons/lib/icons/CloudDownloadOutlined'
import CloseOutlined from '@ant-design/icons/lib/icons/CloseOutlined'
import UploadOutlined from '@ant-design/icons/lib/icons/UploadOutlined'
import Content from 'antd/lib/layout/layout'
import { getFileTypeByExtension } from 'Utils/attributes.utils'
import Flex from 'antd/lib/flex'
import { useTaskContext } from 'Contexts/TaskContext'

const FileAttribute: React.FC<FileAttributeProps> = props => {
    const { className, onChange, width, value, label, readonly } = props
    const { stepId, examId } = useParams()

    const [messageApi] = useMessage()
    const { taskId } = useTaskContext()
    const [isUploading, setIsUploading] = React.useState(false)
    const [isFilePreviewed, setIsFilePreviewed] = React.useState(false)

    // Extract the file path from the value
    const filePath = value && value.length > 0 ? value[0].path : ''

    // Check if the file is an image, audio, or video file based on its extension
    const fileType = useMemo(() => {
        if (value && value.length > 0) {
            return getFileTypeByExtension(value[0].path.toLowerCase())
        }
    }, [value])

    // Retrieves the download URL for the file and initiates the download
    const downloadFile = useCallback(async () => {
        const downloadURL = await getFileDownloadUrl(value![0].path)

        if (downloadURL) {
            downloadPublicFile(downloadURL)
        }
    }, [value])

    // Enable file preview for image, audio, or video files. If the file is not one of these types, it will trigger a download instead.
    const onClickPreviewFile = () => {
        if (['image', 'audio', 'video'].includes(fileType ?? '')) {
            setIsFilePreviewed(isPreviewed => !isPreviewed)
        } else {
            downloadFile()
        }
    }

    // Handles the file upload process, checking the file size and updating the state accordingly
    const handleUpload = async (file: RcFile) => {
        if (file.size > props.maxSizeInGB * 1024 * 1024 * 1024) {
            messageApi.error(`File size exceeds ${props.maxSizeInGB}GB`)
            return
        }
        setIsUploading(true)

        try {
            const res: FileValueType = await uploadFile(
                file,
                `${stepId ?? examId}/${Date.now()}/${file.name}`,
                taskId
            )
            onChange([res])
            messageApi.success('File uploaded successfully')
        } catch (err) {
            messageApi.error('File upload failed')
            console.error(err)
        } finally {
            setIsUploading(false)
        }
    }

    // Memoized file name, which is derived from the file path
    const fileName = useMemo(() => {
        if (filePath) {
            return filePath.split('/').pop() ?? ''
        }
        return ''
    }, [filePath])

    // Memoized content for the file description, which changes based on the upload state and file value
    const descriptionContent = useMemo(() => {
        if (isUploading) {
            return <p className="file-name">Uploading...</p>
        }

        if (value && value.length > 0) {
            const onClickRemoveFile = (e: React.MouseEvent<HTMLSpanElement>) => {
                e.stopPropagation()
                onChange(undefined)
            }

            const onClickDownloadFile: React.MouseEventHandler<HTMLSpanElement> = e => {
                e?.stopPropagation()
                downloadFile()
            }

            return (
                <Flex justify="space-between" align="center" className="file-description-content">
                    <p className="file-name">{fileName}</p>
                    <div className="file-actions-container">
                        <Tooltip trigger="hover" title={'Download'}>
                            <CloudDownloadOutlined
                                className="download-icon"
                                onClick={onClickDownloadFile}
                            />
                        </Tooltip>
                        {!readonly && (
                            <Tooltip trigger="hover" title={'Delete'}>
                                <CloseOutlined className="close-icon" onClick={onClickRemoveFile} />
                            </Tooltip>
                        )}
                    </div>
                </Flex>
            )
        }
    }, [downloadFile, fileName, isUploading, onChange, readonly, value])

    return (
        <div
            className={`attribute-main ${className}`}
            style={{ width: width ?? '90%' }}
            data-testid="file-wrapper"
        >
            <span className="attribute-main-title">{label}</span>
            <Upload
                beforeUpload={file => {
                    handleUpload(file)
                    return false
                }}
                showUploadList={false}
                disabled={readonly}
                maxCount={1}
            >
                <Button
                    icon={<UploadOutlined />}
                    disabled={readonly}
                    className="upload-file-button"
                >
                    Upload
                </Button>
            </Upload>
            {filePath && (
                <Content className={'file-description'} onClick={onClickPreviewFile}>
                    {descriptionContent}
                </Content>
            )}
            {isFilePreviewed && (
                <FilePreview
                    type={fileType!}
                    url={filePath}
                    name={fileName}
                    onClose={() => setIsFilePreviewed(false)}
                />
            )}
        </div>
    )
}

export default FileAttribute
